/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CaretDown, ArrowRight, CheckCircle, ShieldCheck, SignIn,
} from "@phosphor-icons/react";
import { useIsPageEnabled, useIsMenuEnabled } from "../../lib/sitePages";
import { useNavMenu } from "../../lib/navMenus";

export const BTN_DARK = "inline-flex items-center justify-center gap-2 rounded-full px-5 bg-[#0A2540] text-white hover:bg-[#0E3358] transition h-12 text-sm font-medium";
export const BTN_GREEN = "inline-flex items-center justify-center gap-2 rounded-full px-5 bg-[#2D5A27] text-white hover:bg-[#234920] transition h-12 text-sm font-medium";
export const BTN_OUTLINE = "inline-flex items-center justify-center gap-2 rounded-full px-5 bg-white text-slate-800 border border-slate-300 hover:border-slate-400 transition h-12 text-sm font-medium";

// ============================================================================
// PREMIUM MARKETING NAV — shared across all solution/resource pages
// ============================================================================
export const MarketingNav = ({ scrolled = true }) => {
  const [solutions, setSolutions] = React.useState(false);
  const [resources, setResources] = React.useState(false);
  const [signinOpen, setSigninOpen] = React.useState(false);
  // CMS-driven menus
  const mainItems = useNavMenu("main");
  const rawSigninItems = useNavMenu("signin");
  // Defence-in-depth: never render an `/adentry` sign-in link even if
  // it slipped into the CMS. Admin console is direct-entry only.
  const signinItems = React.useMemo(
    () => rawSigninItems.filter(it => it.path !== "/adentry"),
    [rawSigninItems],
  );
  // Menu-visibility flag decides what's rendered in the Solutions/Resources dropdowns.
  const isOn = {
    "/for-government": useIsMenuEnabled("/for-government"),
    "/for-companies": useIsMenuEnabled("/for-companies"),
    "/for-healthcare": useIsMenuEnabled("/for-healthcare"),
    "/newsroom": useIsMenuEnabled("/newsroom"),
    "/white-papers": useIsMenuEnabled("/white-papers"),
    "/csr-sustainability": useIsMenuEnabled("/csr-sustainability"),
    "/security-compliance": useIsMenuEnabled("/security-compliance"),
    "/investors": useIsMenuEnabled("/investors"),
    "/sdg2030": useIsMenuEnabled("/sdg2030"),
  };
  // Page-visibility flag for the "Book a demo" CTA fallback
  const contactPageLive = useIsPageEnabled("/contact");
  const solutionsOn = isOn["/for-government"] || isOn["/for-companies"] || isOn["/for-healthcare"];
  const resourcesOn = isOn["/newsroom"] || isOn["/white-papers"] || isOn["/csr-sustainability"] || isOn["/security-compliance"] || isOn["/investors"] || isOn["/sdg2030"];
  return (
    <header data-testid="marketing-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
              scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/60" : "bg-transparent"
            }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/brand/rescigo-logo.png" alt="Rescigo Health" className="h-9 w-auto"/>
        </Link>
        <nav className="hidden md:flex items-center gap-7 ml-4 text-sm text-slate-700">
          {solutionsOn && (
            <div className="relative" onMouseEnter={() => setSolutions(true)} onMouseLeave={() => setSolutions(false)}>
              <button data-testid="nav-solutions" className="inline-flex items-center gap-1 hover:text-slate-900 h-16">
                Solutions <CaretDown size={11} weight="bold"/>
              </button>
              {solutions && (
                <div className="absolute top-14 left-0 w-64 bg-white rounded-xl shadow-xl ring-1 ring-slate-200/60 p-2">
                  {isOn["/for-government"] && <NavItem to="/for-government" title="For Government" desc="Regulatory intelligence for waste governance" testid="nav-sub-gov"/>}
                  {isOn["/for-companies"] && <NavItem to="/for-companies" title="For Companies" desc="ESG, compliance & waste intelligence" testid="nav-sub-companies"/>}
                  {isOn["/for-healthcare"] && <NavItem to="/for-healthcare" title="For Healthcare" desc="BMW-compliant biomedical infrastructure" testid="nav-sub-healthcare"/>}
                </div>
              )}
            </div>
          )}
          {resourcesOn && (
            <div className="relative" onMouseEnter={() => setResources(true)} onMouseLeave={() => setResources(false)}>
              <button data-testid="nav-resources" className="inline-flex items-center gap-1 hover:text-slate-900 h-16">
                Resources <CaretDown size={11} weight="bold"/>
              </button>
              {resources && (
                <div className="absolute top-14 left-0 w-64 bg-white rounded-xl shadow-xl ring-1 ring-slate-200/60 p-2">
                  {isOn["/newsroom"] && <NavItem to="/newsroom" title="Newsroom" desc="Press releases, company updates"/>}
                  {isOn["/white-papers"] && <NavItem to="/white-papers" title="White Papers" desc="Research & policy frameworks"/>}
                  {isOn["/csr-sustainability"] && <NavItem to="/csr-sustainability" title="CSR & Sustainability" desc="Impact metrics & SDG alignment"/>}
                  {isOn["/sdg2030"] && <NavItem to="/sdg2030" title="SDG 2030 Impact" desc="UN-aligned localization framework" testid="nav-sub-sdg"/>}
                  {isOn["/security-compliance"] && <NavItem to="/security-compliance" title="Security & Compliance" desc="Enterprise-grade data integrity"/>}
                  {isOn["/investors"] && <NavItem to="/investors" title="Investor Relations" desc="Market opportunity & scale"/>}
                </div>
              )}
            </div>
          )}
          {mainItems.map((it) => (
            <Link key={it.path} to={it.path} data-testid={`nav-main-${it.path.replace(/[/]/g,'-')}`}
                  className="hover:text-slate-900">{it.label}</Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {signinItems.length > 0 && (
            <div className="relative hidden sm:block"
                  onMouseEnter={() => setSigninOpen(true)}
                  onMouseLeave={() => setSigninOpen(false)}>
              <button data-testid="nav-signin"
                       className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full hover:bg-slate-100">
                <SignIn size={12} weight="bold"/> Sign in <CaretDown size={10} weight="bold"/>
              </button>
              {signinOpen && (
                <div className="absolute top-10 right-0 w-60 bg-white rounded-xl shadow-xl ring-1 ring-slate-200/60 p-2 z-50">
                  {signinItems.map((it) => (
                    <Link key={it.path} to={it.path}
                           data-testid={`signin-link-${it.path.replace(/[/]/g,'-')}`}
                           className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 hover:text-slate-900">
                      {it.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          <Link to={contactPageLive ? "/contact" : "/#contact"} data-testid="nav-cta-demo"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#0A2540] text-white text-sm font-medium hover:bg-[#0E3358]">
            Book a demo <ArrowRight size={12} weight="bold"/>
          </Link>
        </div>
      </div>
    </header>
  );
};

const NavItem = ({ to, title, desc, testid }) => (
  <Link to={to} data-testid={testid}
        className="block px-3 py-2.5 rounded-lg hover:bg-slate-50 transition">
    <div className="text-sm font-semibold text-slate-900">{title}</div>
    <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
  </Link>
);

// ============================================================================
// MARKETING FOOTER
// ============================================================================
export const MarketingFooter = () => {
  const footerItems = useNavMenu("footer");
  const legalItems = useNavMenu("legal");
  // Ensure /careers always appears in the footer, even if a legacy DB
  // override omitted it. Dedupe by path.
  const explore = React.useMemo(() => {
    const seen = new Set();
    const items = [];
    for (const it of footerItems) {
      if (!seen.has(it.path)) { seen.add(it.path); items.push([it.label, it.path]); }
    }
    if (!seen.has("/careers")) items.push(["Careers", "/careers"]);
    return items;
  }, [footerItems]);
  return (
    <footer className="bg-[#0A2540] text-white pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <img src="/brand/rescigo-logo-white.png" alt="Rescigo" className="h-9 mb-5"/>
          <p className="text-sm text-slate-300 max-w-xs leading-relaxed">
            Enterprise-grade waste intelligence, compliance and public-health infrastructure for India.
          </p>
          <div className="mt-5 text-[11px] uppercase tracking-[0.2em] text-slate-400">ISO 27001 · BMW Rules 2016 · CPCB</div>
        </div>
        <FooterCol h="Explore" testid="footer-explore" items={explore}/>
        <FooterCol h="Legal" testid="footer-legal"
                    items={legalItems.map(it => [it.label, it.path])}/>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-4">Stay updated</div>
          <p className="text-sm text-slate-300 mb-3">Subscribe to product &amp; policy updates.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/20 hover:bg-white/10 transition">
            Get in touch <ArrowRight size={11}/>
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-12 pt-6 border-t border-white/10 text-xs text-slate-400 flex flex-wrap items-center justify-between gap-3">
        <div>© {new Date().getFullYear()} Rescigo Health. All rights reserved.</div>
        <div className="flex items-center gap-2"><ShieldCheck size={12}/> Enterprise-grade security</div>
      </div>
    </footer>
  );
};

const FooterCol = ({ h, items, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-4">{h}</div>
    <ul className="space-y-2.5 text-sm text-slate-300">
      {items.map(([l, to]) => (
        <li key={to}><Link to={to} className="hover:text-white transition">{l}</Link></li>
      ))}
    </ul>
  </div>
);

// ============================================================================
// REUSABLE SECTION HELPERS
// ============================================================================
export const Eyebrow = ({ children, color = "#2D5A27" }) => (
  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color }}>
    <span className="w-6 h-px" style={{ background: color }}/>
    {children}
  </div>
);

export const FeatureCard = ({ Icon, title, desc, accent = "#2D5A27" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
    <div className="w-11 h-11 rounded-lg grid place-items-center mb-5" style={{ background: `${accent}12`, color: accent }}>
      <Icon size={20} weight="duotone"/>
    </div>
    <h3 className="font-heading text-lg text-slate-900">{title}</h3>
    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
  </motion.div>
);

export const KpiTile = ({ label, value, sub, color = "#0A2540" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="font-heading text-3xl md:text-4xl mt-1.5" style={{ color }}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export const BulletList = ({ items, color = "#2D5A27" }) => (
  <ul className="space-y-3">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
        <CheckCircle size={16} weight="fill" className="mt-0.5 flex-none" style={{ color }}/>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export const CtaBanner = ({ eyebrow, title, body, primary, secondary, accent = "#0A2540" }) => (
  <section className="relative overflow-hidden bg-[#0A2540] text-white">
    <div className="absolute inset-0 opacity-10" style={{
      background: "radial-gradient(circle at 20% 30%, #2D5A27 0%, transparent 50%), radial-gradient(circle at 80% 70%, #0E3358 0%, transparent 50%)",
    }}/>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
      <Eyebrow color="#FFFFFF66">{eyebrow}</Eyebrow>
      <h2 className="font-heading text-4xl md:text-5xl mt-4 max-w-3xl leading-tight">{title}</h2>
      {body && <p className="text-lg text-slate-300 mt-5 max-w-2xl leading-relaxed">{body}</p>}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {primary && <Link to={primary.to} data-testid={primary.testid} className={BTN_GREEN}>{primary.label} <ArrowRight size={14} weight="bold"/></Link>}
        {secondary && <Link to={secondary.to} data-testid={secondary.testid} className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-white/30 text-white hover:bg-white/10 transition text-sm font-medium">{secondary.label}</Link>}
      </div>
    </div>
  </section>
);
